/**
 * The two drop areas — the specimen's `kpDrop`: a hairline box, an oversized low-contrast glyph, the
 * instruction with only the verb underlined, and the phone's own constraint lines under it.
 *
 * ── Both zones are live, and share one control ──────────────────────────────────────────────
 * `FileDrop` is the behaviour both have in common: a button that opens the file chooser, takes a
 * dropped file, carries the refusal as its description, and a remove button that hands focus back
 * to the zone. The cover and the film differ only in what they draw inside it and what they accept.
 *
 * The film was drawn and inactive until 25 September 2026 (D15, while the presigned upload waited
 * for a `connect-src` widening and a backend CORS change). The owner's one-pipeline decision made
 * it a control: the film is validated and compressed in the browser, then sent after the save.
 *
 * ── The picture is previewed from a `blob:` url, made and revoked in ONE effect ─────────────
 * `URL.createObjectURL` pins the file in memory until it is revoked. The url is created inside the
 * effect that revokes it and written straight onto the `<img>`, so every url made is revoked exactly
 * once and no render ever holds one that has been revoked. An earlier revision minted it in a
 * `useMemo` and revoked it in an effect: StrictMode's double effect revoked the url the `<img>` was
 * still showing (step 3 → Wstecz → step 3 drew a broken picture) and the double memo leaked one per
 * mount. `img-src` already allows `blob:` in `vite.config.ts`.
 *
 * ── Every file is PREPARED, and what cannot be is refused in the zone ──────────────────────
 * A picture is re-encoded (`~/shared/media/compressCover`: ≤1600 px, JPEG 0.8) and a film compressed
 * (`~/shared/media/video/prepareVideo`: MP4, ≤1280 px, 3 Mbps, ≤50 MiB, ≤2 min) before it becomes
 * the draft's. What cannot be is refused HERE — the uploads are requests after the save, so a
 * refusal there would reach the trainer only after the wizard had closed. The preparation itself
 * is the wizard's (`useCreateExercise`), because a save pressed while it runs has to wait for it;
 * the zone hands over the file and draws the answer.
 *
 * ── The zone's lines are ONE slot, and a refusal takes it rather than growing the zone ──────
 * The lines under the title (the limits, „Wybrany film: …”, how far the compression has got) and a
 * refusal share one grid cell: while a refusal stands the lines stay in the cell, hidden, holding
 * their height. A refusal arrives seconds after the choice (the film is decoded first), and one
 * drawn under the zone pushed Opis and Jak wykonać down by a line while the trainer was reading
 * them. A refusal longer than the lines it replaces still grows the cell. The two zones' columns
 * are `minmax(0, 1fr)` and every line breaks anywhere, so a file name with no spaces wraps inside
 * its zone instead of widening its column. Measured at 1280 × 620 on 25 September 2026 with a
 * 60-character name and the unreadable refusal („Wybierz plik MP4 albo MOV.”): three lines, the
 * zone 163px → 172px, both columns still 430px wide. The browser's refusals (formats, the browsers
 * that work, where to write) are longer and grow the cell further; not yet measured.
 *
 * ── The zone is NAMED by its title and DESCRIBED by its lines ───────────────────────────────
 * Its accessible name is the visible instruction alone. The lines were part of it while they were
 * the button's text, so every percent of a compression renamed the control under a listener's
 * focus. They are its description now, and a status that changes is announced from the wizard's own
 * live region, at milestones only (`WizardFooter`).
 *
 * ── A remove button that goes while focused hands focus to its zone ─────────────────────────
 * „Przerwij” stands only while a film is compressed, so it can vanish under the trainer's focus —
 * the compression ending in a refusal. Focus would drop to the page; it goes to the zone instead.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type DragEvent,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { COVER_ACCEPT, coverRefusalKey, type CoverRefusal } from '~/shared/media/coverFile';
import { VIDEO_ACCEPT, videoRefusalKey } from '~/shared/media/video/videoFile';

import type { RefusedVideo } from '../lib/useCreateExercise';
import { filmZoneStatus } from '../model/saveProgress';

import styles from './CreateExerciseDialog.module.css';

function ImageGlyph({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <rect x="2" y="3" width="12" height="10" rx="2" />
      <circle cx="6" cy="7" r="1.2" />
      <path d="m3 12 3.5-3.5 2.5 2.5 2-2 3 3" />
    </svg>
  );
}

function VideoGlyph({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <rect x="1.5" y="3.5" width="9" height="9" rx="2" />
      <path d="m10.5 7 4-2.2v6.4l-4-2.2z" />
    </svg>
  );
}

interface FileDropProps {
  /** What the file chooser offers. */
  readonly accept: string;
  /** The instruction's first words — „Przeciągnij film albo” — before the underlined verb. */
  readonly lead: string;
  /** The glyph, or the chosen picture. */
  readonly art: ReactNode;
  /** The lines under the instruction, in order. */
  readonly lines: readonly string[];
  /** Why the last file chosen was refused, as a sentence, or `null`. It takes the lines' place. */
  readonly refusal: string | null;
  /** The remove button's label, or `null` while there is nothing to remove. */
  readonly removeLabel: string | null;
  /** The remove button's accessible name, when its label alone is too short to say what it stops. */
  readonly removeName?: string;
  readonly onChoose: (file: File) => void;
  readonly onRemove: () => void;
}

/** A drop zone: click, keyboard or a dropped file, its lines or a refusal, and a way to remove. */
function FileDrop({
  accept,
  lead,
  art,
  lines,
  refusal,
  removeLabel,
  removeName,
  onChoose,
  onRemove,
}: FileDropProps) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const zone = useRef<HTMLButtonElement>(null);
  /* Whether the remove button held focus when it last lost it to nothing — see the header. Written
   * in its focus handlers, read in the effect below. */
  const removeHeldFocus = useRef(false);
  const removing = removeLabel !== null;
  useEffect(() => {
    if (removing || !removeHeldFocus.current) return;
    removeHeldFocus.current = false;
    const active = document.activeElement;
    if (active === null || active === document.body || active.getAttribute('role') === 'dialog') {
      zone.current?.focus();
    }
  }, [removing]);
  const baseId = useId();
  const [over, setOver] = useState(false);
  const verb = t('web:exerciseLibrary.creator.media.choose');
  const lineIds = lines.map((_, index) => `${baseId}-line-${String(index)}`);
  const refusalId = `${baseId}-refusal`;

  const take = (chosen: File | undefined) => {
    if (chosen !== undefined) onChoose(chosen);
  };

  return (
    <div className={`${String(styles.block)} ${String(styles.blockTight)}`}>
      <button
        ref={zone}
        type="button"
        className={`${String(styles.drop)} ${over ? String(styles.dropOver) : ''}`}
        aria-label={`${lead} ${verb}`}
        aria-describedby={refusal === null ? lineIds.join(' ') : refusalId}
        onClick={() => {
          input.current?.click();
        }}
        onDragOver={(event: DragEvent) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => {
          setOver(false);
        }}
        onDrop={(event: DragEvent) => {
          event.preventDefault();
          setOver(false);
          take(event.dataTransfer.files[0]);
        }}
      >
        {art}
        <span className={styles.dropTitle}>
          {lead} <u className={styles.dropVerb}>{verb}</u>
        </span>
        <span className={styles.dropSlot}>
          <span
            className={`${String(styles.dropLines)} ${refusal === null ? '' : String(styles.dropLinesHeld)}`}
          >
            {lines.map((line, index) => (
              <span key={lineIds[index]} id={lineIds[index]} className={styles.dropLine}>
                {line}
              </span>
            ))}
          </span>
          {refusal !== null && (
            <span id={refusalId} className={styles.rowRefusal}>
              {refusal}
            </span>
          )}
        </span>
      </button>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        onChange={(event) => {
          take(event.target.files?.[0]);
          // Cleared, so choosing the same file again after removing it still fires a change.
          event.target.value = '';
        }}
      />
      {removeLabel !== null && (
        <button
          type="button"
          className={styles.textButton}
          aria-label={removeName}
          onFocus={() => {
            removeHeldFocus.current = true;
          }}
          onBlur={(event: FocusEvent) => {
            // Focus moving somewhere is the trainer's; focus lost to nothing is the button going.
            if (event.relatedTarget !== null) removeHeldFocus.current = false;
          }}
          onClick={() => {
            onRemove();
            // This button unmounts with the file; focus goes to the zone it belongs to rather than
            // falling to the popup and restarting the Tab order at the rail.
            zone.current?.focus();
          }}
        >
          {removeLabel}
        </button>
      )}
    </div>
  );
}

interface VideoDropZoneProps {
  /** The film chosen and prepared in this sitting, or `null`. */
  readonly file: File | null;
  /** The movement already has a film (an edit), which a new one replaces. */
  readonly hasCurrent: boolean;
  /** How far the chosen film's compression has got, in whole percent, or `null`. */
  readonly preparingPercent: number | null;
  /** The last film chosen that cannot be sent, and why, or `null`. */
  readonly refusal: RefusedVideo | null;
  readonly onChoose: (file: File) => void;
  readonly onRemove: () => void;
}

/** The film's zone: click, keyboard or a dropped file; compressed before it counts as chosen. */
export function VideoDropZone({
  file,
  hasCurrent,
  preparingPercent,
  refusal,
  onChoose,
  onRemove,
}: VideoDropZoneProps) {
  const { t } = useTranslation();
  const status = filmZoneStatus({ preparingPercent, chosenName: file?.name ?? null, hasCurrent });
  return (
    <FileDrop
      accept={VIDEO_ACCEPT}
      lead={t('web:exerciseLibrary.creator.media.videoVerb')}
      art={<VideoGlyph className={styles.dropGlyph} />}
      lines={[
        t(status.key, status.values),
        t('exerciseLibrary.createWizard.mediaForm.videoProcessingHint'),
      ]}
      refusal={
        refusal === null
          ? null
          : t('web:exerciseLibrary.creator.media.videoRefused', {
              name: refusal.fileName,
              reason: t(videoRefusalKey(refusal.refusal)),
            })
      }
      removeLabel={
        preparingPercent !== null
          ? t('web:exerciseLibrary.creator.media.stopPreparing')
          : file !== null
            ? t('shared.media.removeVideo')
            : null
      }
      removeName={
        preparingPercent !== null ? t('web:exerciseLibrary.creator.media.stopPreparingName') : undefined
      }
      onChoose={onChoose}
      onRemove={onRemove}
    />
  );
}

interface CoverDropZoneProps {
  /** The picture chosen in this sitting, or `null`. */
  readonly file: File | null;
  /** The picture the movement already has, drawn until a new one is chosen. */
  readonly currentUrl: string | null;
  /** Why the last picture chosen cannot be the cover, or `null`. */
  readonly refusal: CoverRefusal | null;
  readonly onChoose: (file: File) => void;
  readonly onRemove: () => void;
}

/** The cover's zone — live: click, keyboard or a dropped file. */
export function CoverDropZone({ file, currentUrl, refusal, onChoose, onRemove }: CoverDropZoneProps) {
  const { t } = useTranslation();
  const preview = useRef<HTMLImageElement>(null);

  // Made and revoked in ONE effect, and handed to the `<img>` directly — see the header.
  useEffect(() => {
    const img = preview.current;
    if (file === null || img === null) return undefined;
    const url = URL.createObjectURL(file);
    img.src = url;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // The PREVIEW is the encoded file the wizard hands back, so what the trainer sees is what is sent.
  return (
    <FileDrop
      accept={COVER_ACCEPT}
      lead={t('web:exerciseLibrary.creator.media.coverVerb')}
      art={
        file === null && currentUrl === null ? (
          <ImageGlyph className={styles.dropGlyph} />
        ) : (
          // The chosen file's `blob:` url is written by the effect above; the movement's own cover
          // is an ordinary url. Keyed by which it is, so the element the effect writes to is fresh.
          <img
            key={file === null ? 'current' : 'chosen'}
            ref={preview}
            className={styles.dropPreview}
            src={file === null ? (currentUrl ?? undefined) : undefined}
            alt=""
          />
        )
      }
      lines={[
        file === null
          ? t('exerciseLibrary.createWizard.mediaForm.photoMeta')
          : t('web:exerciseLibrary.creator.media.coverChosen', { name: file.name }),
      ]}
      refusal={refusal === null ? null : t(coverRefusalKey(refusal))}
      removeLabel={file === null ? null : t('exerciseLibrary.createWizard.mediaForm.coverRemoveA11y')}
      onChoose={onChoose}
      onRemove={onRemove}
    />
  );
}
